import { useEffect, useMemo, useState } from 'react';
import { IconButton, InputAdornment, SxProps, TextField } from '@mui/material';
import * as Icons from '../icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

export type SearchInputProps = {
  fullWidth?: boolean;
  autoFocus?: boolean;
  /**
   * Controlled value. Omit to use uncontrolled mode.
   */
  value?: string;
  /**
   * Initial value for uncontrolled mode.
   */
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  sx?: SxProps;
  startAdornmentSx?: SxProps;
  hasStartAdornment?: boolean;
  InputProps?: object;
  debounceMs?: number;
  /**
   * Optional debounced commit hook. Called after `debounceMs` of inactivity.
   * Use this for expensive operations (filtering, network requests).
   */
  onDebouncedChange?: (value: string) => void;
};

export function SearchInput({
  value,
  defaultValue,
  placeholder,
  onChange,
  onClear,
  fullWidth = false,
  autoFocus = false,
  hasStartAdornment = true,
  startAdornmentSx = {},
  debounceMs = 500,
  onDebouncedChange,
  ...props
}: SearchInputProps) {
  // Support controlled and uncontrolled modes:
  // if parent supplies `value`, that drives the input; otherwise use internal state.
  const [internalValue, setInternalValue] = useState<string>(value ?? defaultValue ?? '');
  const { t } = useTranslation();

  // Keep internal state in sync when the parent provides a changing `value`
  useEffect(() => {
    if (typeof value !== 'undefined' && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  // Debounced commit: ONLY calls onDebouncedChange (noop if not provided).
  const debouncedCommit = useMemo(() => {
    if (!onDebouncedChange || !debounceMs) {
      return {
        call: (v: string) => onChange?.(v),
        cancel: () => {},
      };
    }

    const debounceObject = debounce((v: string) => onDebouncedChange(v), debounceMs);
    return {
      call: debounceObject,
      cancel: () => debounceObject.cancel(),
    };
  }, [onDebouncedChange, debounceMs]);

  // Cleanup on unmount
  useEffect(() => debouncedCommit.cancel, [debouncedCommit]);

  const clearSearch = () => {
    debouncedCommit.cancel();
    setInternalValue('');
    onChange && onChange('');
    onDebouncedChange && onDebouncedChange('');
    onClear && onClear();
  };

  const { InputProps, sx, ...rest } = props || {};
  const currentValue = typeof value !== 'undefined' ? value : internalValue;

  return (
    <TextField
      data-testid="search-input"
      variant="outlined"
      size="small"
      fullWidth={fullWidth}
      value={currentValue}
      label={t('main:search.search')}
      InputLabelProps={{
        shrink: true,
      }}
      onChange={(event) => {
        const val = event.target.value;
        // update internal state for uncontrolled usage
        if (typeof value === 'undefined') {
          setInternalValue(val);
        }

        onChange?.(val);
        debouncedCommit.call(val);
      }}
      placeholder={placeholder}
      InputProps={{
        autoFocus,
        startAdornment: hasStartAdornment && (
          <InputAdornment position="start">
            <Icons.Search sx={{ color: 'grey.700', ...startAdornmentSx }} />
          </InputAdornment>
        ),
        endAdornment: currentValue ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={clearSearch}>
              <Icons.Close
                sx={{ cursor: 'pointer', color: 'grey.700', fontSize: '20px' }}
                data-testid="clear-search-input"
              />
            </IconButton>
          </InputAdornment>
        ) : null,
        ...InputProps,
        sx: { pl: hasStartAdornment ? 1.5 : 0, pr: 0.75 },
      }}
      sx={{
        minWidth: fullWidth ? 'unset' : '245px',
        ...sx,
      }}
      {...rest}
    />
  );
}
